import { lazy } from 'react';
import type { ReactNode } from 'react';
import type { RouteObject } from 'react-router-dom';
import RequirePermission from '@/auth/RequirePermission';
import { Perm } from '@/auth/permissions';
import RequireModule from '@/core/modules/RequireModule';
import { Modules } from '@/core/modules/moduleKeys';

// Pages load when first opened (NFR-04).
const LendingSettingsPage = lazy(() => import('./pages/LendingSettingsPage'));
const LendingProductsPage = lazy(() => import('./pages/LendingProductsPage'));
const LendingProductPage = lazy(() => import('./pages/LendingProductPage'));
const LendingQuotePage = lazy(() => import('./pages/LendingQuotePage'));
const LendingCustomersPage = lazy(() => import('./pages/LendingCustomersPage'));
const LendingCustomerPage = lazy(() => import('./pages/LendingCustomerPage'));
const LendingCompliancePage = lazy(() => import('./pages/LendingCompliancePage'));
const LendingApplicationsPage = lazy(() => import('./pages/LendingApplicationsPage'));
const LendingApplicationPage = lazy(() => import('./pages/LendingApplicationPage'));
const LendingCollateralPage = lazy(() => import('./pages/LendingCollateralPage'));
const LendingScorecardsPage = lazy(() => import('./pages/LendingScorecardsPage'));
const LendingScorecardPage = lazy(() => import('./pages/LendingScorecardPage'));
const LendingLoansPage = lazy(() => import('./pages/LendingLoansPage'));
const LendingLoanPage = lazy(() => import('./pages/LendingLoanPage'));
const LendingDisbursementsPage = lazy(() => import('./pages/LendingDisbursementsPage'));
const LendingServicingPage = lazy(() => import('./pages/LendingServicingPage'));
const LendingEodPage = lazy(() => import('./pages/LendingEodPage'));
const LendingCollectionsPage = lazy(() => import('./pages/LendingCollectionsPage'));
const LendingFinancePage = lazy(() => import('./pages/LendingFinancePage'));
const LendingAmlPage = lazy(() => import('./pages/LendingAmlPage'));
const LendingConductPage = lazy(() => import('./pages/LendingConductPage'));
const LendingReturnsPage = lazy(() => import('./pages/LendingReturnsPage'));
const LendingDashboardPage = lazy(() => import('./pages/LendingDashboardPage'));
const LendingAgentPage = lazy(() => import('./pages/LendingAgentPage'));
const LendingChannelsPage = lazy(() => import('./pages/LendingChannelsPage'));
const LendingPlatformPage = lazy(() => import('./pages/LendingPlatformPage'));
const LendingRecoveriesPage = lazy(() => import('./pages/LendingRecoveriesPage'));

/**
 * The lending module's routes (docs §222 on), spread into the signed-in layout. Every page sits behind `RequireModule`,
 * so an organisation without the module sees the "not enabled" card rather than pages meeting 403s.
 */
const guard = (element: ReactNode) => (
  <RequirePermission permission={Perm.Lending.View}>
    <RequireModule module={Modules.Lending}>{element}</RequireModule>
  </RequirePermission>
);

export const lendingRoutes: RouteObject[] = [
  { path: 'lending/settings/:view?', element: guard(<LendingSettingsPage />) },
  { path: 'lending/products', element: guard(<LendingProductsPage />) },
  { path: 'lending/products/:id', element: guard(<LendingProductPage />) },
  { path: 'lending/quote', element: guard(<LendingQuotePage />) },
  { path: 'lending/customers', element: guard(<LendingCustomersPage />) },
  { path: 'lending/customers/:id', element: guard(<LendingCustomerPage />) },
  { path: 'lending/compliance/:view?', element: guard(<LendingCompliancePage />) },
  { path: 'lending/applications', element: guard(<LendingApplicationsPage />) },
  { path: 'lending/applications/:id', element: guard(<LendingApplicationPage />) },
  { path: 'lending/collateral', element: guard(<LendingCollateralPage />) },
  { path: 'lending/scorecards', element: guard(<LendingScorecardsPage />) },
  { path: 'lending/scorecards/:id', element: guard(<LendingScorecardPage />) },
  { path: 'lending/loans', element: guard(<LendingLoansPage />) },
  { path: 'lending/loans/:id', element: guard(<LendingLoanPage />) },
  { path: 'lending/disbursements/:view?', element: guard(<LendingDisbursementsPage />) },
  { path: 'lending/servicing/:view?', element: guard(<LendingServicingPage />) },
  { path: 'lending/eod/:view?', element: guard(<LendingEodPage />) },
  { path: 'lending/collections/:view?/:id?', element: guard(<LendingCollectionsPage />) },
  { path: 'lending/finance/:view?', element: guard(<LendingFinancePage />) },
  { path: 'lending/aml/:view?', element: guard(<LendingAmlPage />) },
  { path: 'lending/conduct/:view?', element: guard(<LendingConductPage />) },
  { path: 'lending/returns/:view?', element: guard(<LendingReturnsPage />) },
  { path: 'lending/dashboard', element: guard(<LendingDashboardPage />) },
  { path: 'lending/agent', element: guard(<LendingAgentPage />) },
  { path: 'lending/channels/:view?', element: guard(<LendingChannelsPage />) },
  { path: 'lending/platform/:view?', element: guard(<LendingPlatformPage />) },
  { path: 'lending/recoveries/:view?', element: guard(<LendingRecoveriesPage />) },
];
